/**
 * Chat-feature API calls against the amended v2 contract (protocol/api.md,
 * DECISIONS #62).
 *
 * These live inside features/chat (WS-H-owned) rather than api/queries.ts
 * (WS-G-owned): the chat surface — session create with model/effort
 * overrides, the settings/rename PATCH, export — is chat-experience
 * machinery. Reads (product detail, repo status) go through the WS-G query
 * registry so every consumer shares ONE cache entry per key.
 */

import { apiPatch, apiPost } from '../../api/client';
import { useRepoStatus as useRepoStatusQuery } from '../../api/queries';
import type {
  ChatSessionCreated,
  ChatSessionSummary,
  CreateChatSessionRequest,
  ExportRequest,
  ExportResponse,
  PatchChatSessionRequest,
  Ulid,
} from '../../api/types';

/** POST /api/products/{pid}/chat/sessions — model/effort overrides ride the body. */
export function createChatSession(
  productId: Ulid,
  body: CreateChatSessionRequest,
): Promise<ChatSessionCreated> {
  return apiPost<ChatSessionCreated>(`/products/${productId}/chat/sessions`, body);
}

/**
 * PATCH /api/chat/sessions/{sid} — rename + model/effort overrides.
 * Settings changes answer 409 generation_active while a turn streams.
 */
export function patchChatSession(
  sessionId: Ulid,
  body: PatchChatSessionRequest,
): Promise<ChatSessionSummary> {
  return apiPatch<ChatSessionSummary>(`/chat/sessions/${sessionId}`, body);
}


/** POST /api/chat/sessions/{sid}/export — one internal agent turn. */
/** The reader's Stop (2026-09-07): the server cancels the running turn and
 * keeps what it said; 204 whether or not one was running. */
export function stopChatTurn(sessionId: Ulid): Promise<void> {
  return apiPost<void>(`/chat/sessions/${sessionId}/stop`);
}

export function exportChatSession(sessionId: Ulid, body: ExportRequest): Promise<ExportResponse> {
  return apiPost<ExportResponse>(`/chat/sessions/${sessionId}/export`, body);
}

/**
 * GET /api/repos/{rid}/status — feeds the compact provenance lines
 * (DESIGN2 §9). The shared WS-G query (key `['repo', rid, 'status']`).
 */
export const useRepoStatus = useRepoStatusQuery;
